<template>
    <li
        v-if="ticket.event && ticket.odds && ticket.priceOption"
        class="list notranslate"
    >
        <SportIcon
            v-bind:sportType="ticket.event.sportType"
            v-bind:colorful="true"
            v-bind:size="20"
        />
        <div class="list_content">
            <p class="teamVsName">
                {{ homeTeamName }} - {{ awayTeamName }}
            </p>
            <p>
                <span>{{ i18n.t(ticket.odds.marketDisplayTypeName) }}</span>
                <span>|</span>
                <strong v-bind:class="isDisplayFavorites ? 'team-favorite' : ''">{{ betOptionDisplayName }}</strong>
                <span
                    v-if="isDisplayPoint"
                    class="oddsPoint"
                >{{ toFixedDecimalPoint(point, 2) }}</span>
            </p>
        </div>
    </li>
</template>

<script lang="ts" setup>
    import type { PropType } from 'vue';
    import { computed } from 'vue';
    import SportIcon from '@/components/common/SportIcon.vue';
    import type { IMpResolvedTicket } from '@/interface/IPopularMixParlay';
    import { MarketType } from '@/interface/enum';
    import { getRealPoint } from '@/libraries/composables/useOdds';
    import { OptionDisplayType, getBetOptionName } from '@/libraries/helpers/betOptionDisplayHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { toFixedDecimalPoint } from '@/libraries/utils/text';

    const props = defineProps({
        ticket: {
            type: Object as PropType<IMpResolvedTicket>,
            required: true,
        },
    });

    const betOptionDisplayName = computed(() => {
        if (!props.ticket.event || !props.ticket.odds || !props.ticket.priceOption) {
            return '';
        }

        return getBetOptionName(props.ticket.event, props.ticket.odds, props.ticket.priceOption, OptionDisplayType.TeamName);
    });

    const homeTeamName = computed(() => getTranslated(props.ticket.event?.homeTeam?.teamName));
    const awayTeamName = computed(() => getTranslated(props.ticket.event?.awayTeam?.teamName));

    const point = computed(() => {
        if (!props.ticket.event || !props.ticket.odds || !props.ticket.priceOption) return '';

        return getRealPoint(props.ticket.event, props.ticket.odds, props.ticket.priceOption);
    });
    const isDisplayPoint = computed(() => point.value !== '');

    const displayFavoritesMarket = new Set([
        MarketType.Handicap,
        MarketType.FH_Handicap,
        MarketType._1X2,
        MarketType.FH_1X2,
        MarketType.MoneyLine,
    ]);

    const homeOptions = new Set(['h', '1']);
    const awayOptions = new Set(['a', '2']);
    const isDisplayFavorites = computed(() => {
        if (displayFavoritesMarket.has(props.ticket.odds?.marketType as MarketType)) {
            if (homeOptions.has(props.ticket.option) && props.ticket.event?.isHomeFavorite) {
                return true;
            }
            if (awayOptions.has(props.ticket.option) && props.ticket.event?.isAwayFavorite) {
                return true;
            }
        }

        return false;
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/quickCombinationTicket";
</style>
